"use client";

import { z } from "zod";

import { type Prisma, type File } from "@/.zenstack/logical-prisma-client";
import { AddressSchema, HousingDetailsSchema } from "@/.zenstack/zod/models";
import { CrossValidationErrorSchema } from "@/lib/validation/schemas/cross-validation-enhanced.schema";
import {
  AuctionSchedulingErrorSchema,
  LeaseSchedulingErrorSchema,
} from "@/lib/validation/schemas/scheduling-enhanced.schema";

export type ListingEditorType = Prisma.ListingGetPayload<{
  include: {
    business: {
      include: {
        organization: {
          include: {
            members: {
              select: {
                userId: true;
                role: true;
              };
            };
          };
        };
      };
    };
    forLease: { include: { tenantScreeningCriteria: true } };
    atAuction: true;
  };
}>;

/**
 * Maps step names to user-friendly display names
 */
export function getStepDisplayName(step: ListingEditorStep): string {
  const metadata = ListingEditorStepMetadata[step];

  if (!metadata) {
    return step.replace("-", " ").replace(/\b\w/g, (l) => l.toUpperCase());
  }

  return metadata.displayName;
}

// Form metadata for each step - unified schema and field mapping
export interface FormMetadata {
  displayName: string;
  schema: z.ZodSchema<any>;
  formFields: string[];
}

// Extended type for validation that includes files
export type ListingValidationType = ListingEditorType & {
  files?: File[];
};

// Types for validation system
export type ValidationSeverity = "error" | "warning" | "info";

// Type-safe field path system - simplified approach
export type FieldPath<_T> = string; // Simplified for now - can be made more strict later

// Type-safe field mapping that connects to Prisma data model
export interface FieldMapping<T = ListingValidationType> {
  step: ListingEditorStep;
  fields: Array<FieldPath<T>>;
}

// Generic validation rule that's type-safe based on fieldMapping
export interface ValidationRule<T = ListingValidationType> {
  id?: string;
  severity: ValidationSeverity;
  message: string;
  field?: string;
  step?: ListingEditorStep;
  // Type-safe validate function that receives data matching the fieldMapping
  // Note: validation functions should use optional chaining (?.) to handle undefined properties
  validate?: (data: T) => boolean;
  fieldMapping?: FieldMapping<T>[];
}

// Re-export for backward compatibility - moved to avoid circular dependency
// This will be populated by the ListingEditor component when it initializes
export const StepValidationSchemas: Partial<
  Record<ListingEditorStep, z.ZodSchema<any>>
> = {};

// Function to populate StepValidationSchemas from ListingEditorStepMetadata
export function populateStepValidationSchemas(
  stepMetadata: Record<
    ListingEditorStep,
    { schema: z.ZodSchema<any>; formFields: string[] }
  >,
): void {
  Object.entries(stepMetadata).forEach(([step, metadata]) => {
    if (metadata.schema) {
      (StepValidationSchemas as any)[step] = metadata.schema;
    }
  });
}

/**
 * Listing editor step definitions
 */

export enum ListingEditorStep {
  LOCATION = "location",
  PROPERTY_INFO = "property-info",
  AMENITIES = "amenities",
  MEDIA = "media",
  DESCRIPTION = "description",
  AUCTION = "auction",
  FOR_LEASE = "for-lease",
  SUMMARY = "summary",
}

// Unified step metadata mapping - DRY approach combining schemas and field mappings
export const ListingEditorStepMetadata: Record<
  ListingEditorStep,
  FormMetadata
> = {
  [ListingEditorStep.LOCATION]: {
    displayName: "Location",
    schema: AddressSchema as unknown as z.ZodSchema<any>,
    formFields: [
      // TODO: Can these be calculated based on the schema?
      "details.address.streetLine1",
      "details.address.streetLine2",
      "details.address.city",
      "details.address.state",
      "details.address.zipCode",
      "details.address.country",
      "details.address.location.latitude",
      "details.address.location.longitude",
    ],
  },
  [ListingEditorStep.PROPERTY_INFO]: {
    displayName: "Property Information",
    schema: HousingDetailsSchema.pick({
      bedrooms: true,
      bathrooms: true,
      squareFootage: true,
    }) as unknown as z.ZodSchema<any>,
    formFields: [
      "details.housingDetails.bedrooms",
      "details.housingDetails.bathrooms",
      "details.housingDetails.squareFootage",
    ],
  },
  [ListingEditorStep.AMENITIES]: {
    displayName: "Amenities",
    schema: HousingDetailsSchema.pick({
      amenities: true,
    }) as unknown as z.ZodSchema<any>,
    formFields: ["details.housingDetails.amenities"],
  },
  [ListingEditorStep.MEDIA]: {
    displayName: "Media",
    schema: z.object({
      files: z
        .array(z.any(), "Please upload valid image files")
        .min(1, "At least one photo is required"),
    }),
    formFields: [
      "files", // Map validation rules that use fields: ["files"]
    ],
  },
  [ListingEditorStep.DESCRIPTION]: {
    displayName: "Description",
    schema: z.object({
      title: z
        .string("Please enter a valid listing title")
        .min(5, "Title must be at least 5 characters")
        .max(100, "Title must be less than 100 characters"),
      // Description is Prisma JsonValue (Plate.js editor format - array of nodes)
      // Use z.unknown() to accept any JSON structure (array or object)
      description: z.unknown() as z.ZodType<Prisma.JsonValue>,
    }),
    formFields: ["title", "description"],
  },
  [ListingEditorStep.AUCTION]: {
    displayName: "Auction Details",
    // Use the same schema as SchedulingForm for consistency
    // Note: The schema expects the atAuction object directly, not wrapped in nullable
    schema: AuctionSchedulingErrorSchema,
    formFields: [
      "atAuction.auctionStartDate",
      "atAuction.auctionEndDate",
      "atAuction.startingBid",
      "atAuction.bidIncrement",
      "atAuction.reserveBidAmount",
    ],
  },
  [ListingEditorStep.FOR_LEASE]: {
    displayName: "Lease Details",
    // Use the same schema as SchedulingForm for consistency
    // Note: The schema expects the forLease object directly, not wrapped in nullable
    schema: LeaseSchedulingErrorSchema,
    formFields: [
      "forLease.availableFrom",
      "forLease.leaseTermMonths",
      "forLease.tenantScreeningCriteria.isEnabled",
      "forLease.tenantScreeningCriteria.creditScoreRange",
      "forLease.tenantScreeningCriteria.annualIncome",
      "forLease.tenantScreeningCriteria.mostRecentEviction",
    ],
  },
  [ListingEditorStep.SUMMARY]: {
    displayName: "Summary",
    // Cross-validation errors for multi-form business rules
    // Validates relationships between auction and lease schedules
    schema: CrossValidationErrorSchema,
    formFields: [
      // Maps to the actual cross-form validations
      "atAuction.auctionEndDate",
      "forLease.availableFrom",
    ],
  },
};
// Populate the validation schemas to avoid circular dependency
populateStepValidationSchemas(ListingEditorStepMetadata);

export const ListingEditorStepValues = Object.values(ListingEditorStep);
